// frontend/src/components/CookieBanner.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguageStore } from '../store/useLanguageStore';
import { Cookie, X } from 'lucide-react';

const CONSENT_KEY = 'dfk_cookie_consent'; // 'accepted' | 'rejected' | null

/**
 * Actualiza el consent mode de GA4.
 * - 'granted': GA4 usa cookies completas (identifica sesiones, usuarios).
 * - 'denied':  GA4 recopila datos anónimos/agregados (cookieless pings).
 */
const updateGtagConsent = (granted) => {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: granted ? 'granted' : 'denied'
    });
  }
};

export const CookieBanner = () => {
  const { t } = useLanguageStore();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Solo mostrar si el usuario nunca respondió
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      // Pequeño delay para no competir con el render inicial
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
    // Si ya aceptó antes, asegurar que GA4 lo sabe
    if (consent === 'accepted') {
      updateGtagConsent(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    updateGtagConsent(true);
    setVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem(CONSENT_KEY, 'rejected');
    updateGtagConsent(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-[9999] p-4 animate-in slide-in-from-bottom-4 fade-in duration-500">
      <div className="max-w-3xl mx-auto bg-slate-900 text-white rounded-2xl shadow-2xl p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start gap-4">

          {/* Ícono + Texto */}
          <div className="flex-1 flex items-start gap-3">
            <Cookie className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm leading-relaxed">
                {t('cookies.banner_text')}
              </p>
              <Link
                to="/cookies"
                className="text-xs text-blue-400 hover:text-blue-300 font-medium mt-1 inline-block"
              >
                {t('cookies.banner_policy_link')}
              </Link>
            </div>
          </div>

          {/* Botones */}
          <div className="flex items-center gap-2 w-full sm:w-auto flex-shrink-0">
            <button
              onClick={handleReject}
              className="flex-1 sm:flex-none px-4 py-2 text-sm font-bold text-slate-300 bg-slate-800 border border-slate-700 rounded-xl hover:bg-slate-700 transition-all"
            >
              {t('cookies.banner_reject')}
            </button>
            <button
              onClick={handleAccept}
              className="flex-1 sm:flex-none px-4 py-2 text-sm font-bold text-slate-900 bg-white rounded-xl hover:bg-slate-100 transition-all"
            >
              {t('cookies.banner_accept')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
